import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import Icon from '../../components/AppIcon';
import AddProjectModal from './components/AddProjectModal';
import ProjectFilters from './components/ProjectFilters';
import ProjectPipeline from './components/ProjectPipeline';
import ProjectDetails from './components/ProjectDetails';
import PermissionDenied from '../../components/ui/PermissionDenied';
import SkeletonCard from '../../components/ui/SkeletonCard';
import EmptyState from '../../components/ui/EmptyState';
import { usePermissions } from '../../hooks/usePermissions';
import { projectService } from '../../services/projectService';
import { calendarService } from '../../services/calendarService';
import { exportToCSV, PROJECT_CSV_COLUMNS } from '../../utils/exportToCSV';
import SEO from '../../components/SEO';

const Projects = () => {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [selectedProject, setSelectedProject] = useState(null);
  const [draggedProject, setDraggedProject] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [preSelectedCustomer, setPreSelectedCustomer] = useState(null);
  const [permDeniedMsg, setPermDeniedMsg] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    aduType: 'all',
    status: 'all',
    priority: 'all'
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();

    const handleOpenProjectModal = (event) => {
      if (!canCreate) return;
      setPreSelectedCustomer(event?.detail || null);
      setShowAddModal(true);
    };

    const handleOpenProjectDetails = async (event) => {
      const { projectId } = event?.detail || {};
      if (projectId) {
        try {
          const project = await projectService?.getById(projectId);
          if (project) {
            setSelectedProject(project);
          }
        } catch (error) {
          console.error('Error loading project:', error);
        }
      }
    };

    window.addEventListener('openProjectModal', handleOpenProjectModal);
    window.addEventListener('openProjectDetails', handleOpenProjectDetails);

    return () => {
      window.removeEventListener('openProjectModal', handleOpenProjectModal);
      window.removeEventListener('openProjectDetails', handleOpenProjectDetails);
    };
  }, [canCreate]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const data = await projectService?.getAll();
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const showPermDenied = (msg) => {
    setPermDeniedMsg(msg);
    setTimeout(() => setPermDeniedMsg(''), 4000);
  };

  const handleAddProject = () => {
    if (!canCreate) {
      showPermDenied('You have viewer access only. Contact an admin to add projects.');
      return;
    }
    setShowAddModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      aduType: 'all',
      status: 'all',
      priority: 'all'
    });
  };

  const getFilteredProjects = () => {
    return projects?.filter((project) => {
      const matchesSearch = project?.customerName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      project?.address?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesAduType = filters?.aduType === 'all' || project?.aduType === filters?.aduType;
      const matchesStatus = filters?.status === 'all' || project?.phase === filters?.status;
      const matchesPriority = filters?.priority === 'all' || project?.priority === filters?.priority;

      return matchesSearch && matchesAduType && matchesStatus && matchesPriority;
    });
  };

  const handleDragStart = (e, project) => {
    if (!canEdit) return;
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newPhase) => {
    e?.preventDefault();
    if (!canEdit) {
      showPermDenied('You do not have permission to move projects between phases.');
      setDraggedProject(null);
      return;
    }
    if (draggedProject && draggedProject?.phase !== newPhase) {
      handlePhaseUpdate(draggedProject?.id, newPhase);
    }
    setDraggedProject(null);
  };

  const handlePhaseUpdate = async (projectId, newPhase) => {
    try {
      await projectService?.update(projectId, { phase: newPhase });
      const updatedProject = projects?.find(p => p?.id === projectId);
      if (updatedProject) {
        calendarService?.syncProjectMilestones(projectId, {
          phase: newPhase,
          customerName: updatedProject?.customerName,
          address: updatedProject?.address,
          dueDate: updatedProject?.dueDate
        });
      }
      loadProjects();
    } catch (error) {
      console.error('Error updating project phase:', error);
    }
  };

  const handleUpdateContractor = (projectId, contractorValue) => {
    console.log(`Assigning contractor ${contractorValue} to project ${projectId}`);
  };

  const handleUploadDocument = (projectId) => {
    console.log(`Uploading document for project ${projectId}`);
  };

  const handleScheduleInspection = (projectId) => {
    console.log(`Scheduling inspection for project ${projectId}`);
  };

  const handleProjectSuccess = () => {
    loadProjects();
    setShowAddModal(false);
    setPreSelectedCustomer(null);
  };

  const handleProjectUpdate = () => {
    loadProjects();
  };

  const filteredProjects = getFilteredProjects();
  const totalProjects = filteredProjects?.length;
  const activeProjects = filteredProjects?.filter((p) => p?.phase !== 'completion')?.length;
  const completedProjects = filteredProjects?.filter((p) => p?.phase === 'completion')?.length;

  const handleExportCSV = () => {
    const exportData = filteredProjects?.map(p => ({
      ...p,
      budget: p?.budget ? `${Number(p?.budget)?.toLocaleString()}` : '',
      dueDate: p?.dueDate ? new Date(p.dueDate)?.toLocaleDateString() : ''
    }));
    exportToCSV(exportData, PROJECT_CSV_COLUMNS, `projects_export_${new Date()?.toISOString()?.slice(0, 10)}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="ADU Projects Pipeline — Construction Project Management"
        description="Manage your ADU construction projects from planning to completion. Track project phases, assign contractors, monitor timelines, and manage budgets with ADU Flow."
        canonical="/projects"
        keywords="ADU projects, construction project management, project pipeline, ADU construction, project tracking"
        noIndex={true}
      />
      <Header />
      <QuickAddMenu />
      <main className="main-content">
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Project Pipeline
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Manage ADU construction projects across all phases
                </p>
              </div>
              <div className="flex items-center gap-3">
                {canCreate && (
                  <button
                    onClick={handleAddProject}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="Plus" size={16} />
                    Add Project
                  </button>
                )}
                <button
                  onClick={handleExportCSV}
                  disabled={!filteredProjects?.length}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border text-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="Download" size={16} />
                  Export CSV
                </button>
              </div>
            </div>

            {permDeniedMsg && (
              <PermissionDenied message={permDeniedMsg} className="mb-4" />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {loading ? (
                <SkeletonCard variant="stat" count={3} />
              ) : (
                <>
                  <div className="bg-card rounded-xl p-4 sm:p-5 md:p-6 border border-border">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Briefcase" size={22} color="var(--color-primary)" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">Total Projects</p>
                        <p className="text-2xl sm:text-3xl font-bold text-foreground">{totalProjects}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-4 sm:p-5 md:p-6 border border-border">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="HardHat" size={22} color="var(--color-accent)" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">Active Projects</p>
                        <p className="text-2xl sm:text-3xl font-bold text-foreground">{activeProjects}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-4 sm:p-5 md:p-6 border border-border">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle2" size={22} color="var(--color-success)" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">Completed</p>
                        <p className="text-2xl sm:text-3xl font-bold text-foreground">{completedProjects}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <ProjectFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters} />
            
          </div>

          <div className="relative">
            {loading ? (
              <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4">
                {[1, 2, 3, 4]?.map((col) => (
                  <div key={col} className="flex-shrink-0 w-80 md:w-96">
                    <div className="bg-muted/50 rounded-xl p-4 md:p-5">
                      <div className="flex items-center gap-3 mb-5 animate-pulse">
                        <div className="w-9 h-9 rounded-lg bg-muted" />
                        <div>
                          <div className="h-4 bg-muted rounded w-20 mb-1" />
                          <div className="h-3 bg-muted rounded w-12" />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <SkeletonCard variant="project" count={col === 1 ? 3 : col === 2 ? 2 : 1} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProjects?.length === 0 ? (
              <div className="bg-card border border-border rounded-xl">
                <EmptyState
                  variant={filters?.search || filters?.aduType !== 'all' || filters?.status !== 'all' || filters?.priority !== 'all' ? 'projects-filtered' : 'projects'}
                  canCreate={canCreate}
                  onAction={canCreate ? handleAddProject : undefined}
                  actionLabel={canCreate ? "Add Project" : undefined}
                />
              </div>
            ) : (
              <ProjectPipeline
                projects={filteredProjects}
                selectedProject={selectedProject}
                onSelectProject={setSelectedProject}
                onDragStart={canEdit ? handleDragStart : undefined}
                onDragOver={canEdit ? handleDragOver : undefined}
                onDrop={canEdit ? handleDrop : undefined} />
            )}
          </div>
        </div>
      </main>
      {selectedProject &&
      <ProjectDetails
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onUpdateContractor={canEdit ? handleUpdateContractor : undefined}
        onUploadDocument={handleUploadDocument}
        onScheduleInspection={handleScheduleInspection}
        onUpdate={handleProjectUpdate}
        canEdit={canEdit}
        canDelete={canDelete} />
      }
      <AddProjectModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setPreSelectedCustomer(null);
        }}
        onSuccess={handleProjectSuccess}
        preSelectedCustomer={preSelectedCustomer}
      />
    </div>
  );

};

export default Projects;